import { useState } from "react";
import { X, Star, Heart } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";

const T = {
  es: {
    title: "DEJÁ TU RESEÑA",
    sub: "¿Te gustó Nica Bros? ¡Contanos! Tu reseña aparece en la página principal.",
    rate: "Tu calificación",
    name: "Tu nombre (opcional)",
    placeholder: "Escribí lo que más te gustó del juego…",
    submit: "PUBLICAR RESEÑA",
    pickStars: "Tocá las estrellas para calificar",
    thanksTitle: "¡GRACIAS POR TU APOYO!",
    thanksBody: "Tu reseña ya es pública. ¡Pulpería pinolera agradecida! 🇳🇮",
    close: "CERRAR",
  },
  en: {
    title: "LEAVE A REVIEW",
    sub: "Did you enjoy Nica Bros? Tell us! Your review shows on the home page.",
    rate: "Your rating",
    name: "Your name (optional)",
    placeholder: "Tell us what you loved about the game…",
    submit: "POST REVIEW",
    pickStars: "Tap the stars to rate",
    thanksTitle: "THANK YOU FOR YOUR SUPPORT!",
    thanksBody: "Your review is now public. ¡Gracias, pinolero! 🇳🇮",
    close: "CLOSE",
  },
};

export default function ReviewModal({ onClose, onSubmitted, intro }) {
  const { lang } = useLang();
  const { user } = useAuth();
  const t = T[lang] || T.es;
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [name, setName] = useState(user?.username || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (rating < 1) { toast.error(t.pickStars); return; }
    setBusy(true);
    try {
      await api.createReview({ rating, text: text.trim(), name: name.trim() });
      setDone(true);
      onSubmitted && onSubmitted();
    } catch (e) { toast.error("Error"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 overflow-y-auto overscroll-contain flex items-start justify-center p-4" data-testid="review-modal">
      <div className="pixel-card bg-[#141414] w-full max-w-md p-6 relative max-h-full overflow-y-auto pop-in my-auto">
        <button data-testid="review-close-btn" onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white"><X size={22} /></button>

        {done ? (
          <div className="text-center py-4" data-testid="review-thanks">
            <Heart size={42} className="mx-auto text-[#FF1744]" fill="#FF1744" />
            <h2 className="font-pixel text-base text-[#FFEB3B] pixel-stroke mt-3">{t.thanksTitle}</h2>
            <p className="font-vt text-lg text-white/80 mt-2">{t.thanksBody}</p>
            <button data-testid="review-thanks-close" onClick={onClose} className="pixel-btn bg-[#4CAF50] text-white px-8 py-3 text-sm mt-5">{t.close}</button>
          </div>
        ) : (
          <>
            <h2 className="font-pixel text-base text-[#FFEB3B] pixel-stroke">⭐ {t.title}</h2>
            <p className="font-vt text-lg text-white/65 mt-2">{intro || t.sub}</p>

            <div className="mt-4">
              <div className="font-vt text-sm text-white/50 mb-1">{t.rate}</div>
              <div className="flex gap-1.5" data-testid="review-stars" onMouseLeave={() => setHover(0)}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`review-star-${n}`} onMouseEnter={() => setHover(n)} onClick={() => setRating(n)}
                    className="transition-transform hover:scale-110">
                    <Star size={34} strokeWidth={2}
                      className={(hover || rating) >= n ? "text-[#FFC107]" : "text-white/25"}
                      fill={(hover || rating) >= n ? "#FFC107" : "none"} />
                  </button>
                ))}
              </div>
            </div>

            <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} maxLength={500}
              placeholder={t.placeholder} rows={3}
              className="w-full mt-4 px-3 py-2 rounded-xl bg-[#0c0c14] border border-white/15 text-white font-vt text-base outline-none resize-none" />

            <input data-testid="review-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={30}
              disabled={!!user?.username} placeholder={t.name}
              className="w-full mt-3 px-3 py-2 rounded-xl bg-[#0c0c14] border border-white/15 text-white font-vt text-base outline-none disabled:opacity-60" />

            <button data-testid="review-submit-btn" onClick={submit} disabled={busy}
              className="pixel-btn bg-[#FF5722] text-white w-full px-6 py-3.5 text-sm mt-5 disabled:opacity-60">
              {t.submit}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
